import React from "react";
import firebase from "firebase/app";
import { RouteProp, useNavigation } from "@react-navigation/native";
import algoliasearch from "algoliasearch";
import { InstantSearch, useHits, useRefinementList } from "react-instantsearch-hooks";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native";
import { useSelector } from "react-redux";
import moment from "moment";
import times from "lodash/times";
import chunk from "lodash/chunk";

import * as connectionDb from "src/db/connection.db";
import { CategoryRefinement, SearchBox, SearchPagination, SortBy } from "src/components";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { ALGOLIA_APP_ID, ALGOLIA_PRODUCTS, ALGOLIA_SEARCH_KEY } from "../env";
import { kRoute, tRouteParams } from "src/kRoute";
import { useFetchBrandLanding } from "src/redux/BrandLanding/actions";
import { formatMinMaxMoney, formatMoney, formatPercentage, kBrandEthosConversion } from "src/util/gFormatting";
import { fonts } from "../fonts";
import { AttachmentIcon } from "src/components/impComponents";
import {
  tAlgoliaProduct,
  tAnnouncement,
  tBrandPressPublication,
  tConnectionStatus,
  tDetailedCart,
  tPromotion,
  tThread,
  tUserConnection,
} from "tmt_api/src/types/types";
import { SLImage } from "src/components/ReactiveImage";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { kFonts } from "src/kFonts";
import { addPromoCode } from "src/redux/user/actions";
import { setCurrentThread } from "src/redux/messages/actions";
import {
  PdAdd,
  PdAnnounce,
  PdArrowUpRight,
  PdCheck,
  PdFacebookAlt,
  PdGiftBox,
  PdInstagram,
  PdPlayFill,
} from "src/components/Icons";
import api from "src/util/api";
import { AddProductModal, tAddProductModal } from "src/components/Modals";
import { useModal } from "src/providers/OverlayProvider";
import { useBrandReadMoreModal, useConnectionRequestModal } from "src/hooks/modals";
import { useToast } from "src/hooks/toast";
import { isApiError } from "src/util/apiNew";
import { PdStack, PdTag, PdTagLabel, PdText } from "src/components/layout";
import { useAppDispatch } from "src/redux";
import { Carousel } from "src/components/ui";
import { ShopSubHeader } from "src/components/connected";
import {
  PdProfileCard,
  PdProfileSocialIconButton,
  PdProfileTag,
  PdProfileTagLabel,
} from "src/components/layout/display";
import { formatCurrency } from "tmt_api/src/util/formatters";
import { getSafeUrl } from "src/util/functions";
import { openUrlInNewTab } from "src/util/linking";
import { queryBrandAnnouncements, queryBrandPromotions } from "src/db/brand.db";

const kNilUuid = "00000000-0000-0000-0000-000000000000";

const dPressLogosUrls: { [key in tBrandPressPublication]: string } = {
  Allure: "/images/press-logos/allure.png",
  "Beauty Independent": "/images/press-logos/beauty_independent.png",
  "Bon Appetit": "/images/press-logos/bon_appetit.png",
  Bustle: "/images/press-logos/bustle.png",
  Byrdie: "/images/press-logos/byrdie.png",
  Cosmopolitan: "/images/press-logos/cosmopolitan.png",
  Coveteur: "/images/press-logos/coveteur.png",
  Elle: "/images/press-logos/elle.png",
  Essence: "/images/press-logos/essence.png",
  Forbes: "/images/press-logos/forbes.png",
  Glamour: "/images/press-logos/glamour.png",
  Glossy: "/images/press-logos/glossy.png",
  Goop: "/images/press-logos/goop.png",
  GQ: "/images/press-logos/gq.png",
  "Harpers Bazaar": "/images/press-logos/harpers_bazaar.png",
  InStyle: "/images/press-logos/instyle.png",
  "Into the Gloss": "/images/press-logos/into_the_gloss.png",
  "Marie Claire": "/images/press-logos/marie_claire.png",
  "Martha Stewart": "/images/press-logos/martha_stewart.png",
  Medium: "/images/press-logos/medium.png",
  "Modern Retail": "/images/press-logos/modern_retail.png",
  "New Beauty": "/images/press-logos/new_beauty.png",
  "New York Post": "/images/press-logos/new_york_post.png",
  "Oprah Daily": "/images/press-logos/oprah_daily.png",
  People: "/images/press-logos/people.png",
  "Retail Brew": "/images/press-logos/retail_brew.png",
  Refinery29: "/images/press-logos/refinery29.png",
  Self: "/images/press-logos/self.png",
  "Southern Living": "/images/press-logos/southern_living.png",
  "The Business of Fashion": "/images/press-logos/the_business_of_fashion.png",
  "The Cut": "/images/press-logos/the_cut.png",
  "The New York Times": "/images/press-logos/the_new_york_times.png",
  "The Washington Post": "/images/press-logos/the_washington_post.png",
  "The Zoe Report": "/images/press-logos/the_zoe_report.png",
  "Town & Country": "/images/press-logos/town_and_country.png",
  "US Weekly": "/images/press-logos/us_weekly.png",
  "Vanity Fair": "/images/press-logos/vanity_fair.png",
  Vogue: "/images/press-logos/vogue.png",
  "Who What Wear": "/images/press-logos/who_what_wear.png",
  "Well Good": "/images/press-logos/well_good.png",
  "Women's Health": "/images/press-logos/womens_health.png",
  WWD: "/images/press-logos/wwd.png",
};

const useGridLayout = () => {
  const { width } = useWindowDimensions();
  let numColumns = 4;
  if (width >= 1200 && width < 1600) numColumns = 3;
  if (width >= 600 && width < 1200) numColumns = 2;
  if (width < 600) numColumns = 1;
  return numColumns;
};

const useIsLargeScreenSelector = () => {
  return useSelector((state) => state.NavigationReducer.deviceSize === "lg");
};

const useConnectionStatusSelector = () => {
  const rBrandId = useSelector((state) => state.BrandLandingReducer.brandData.id);
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const [status, setStatus] = React.useState<tConnectionStatus | undefined>(undefined);

  React.useEffect(() => {
    const unsubscribe = connectionDb
      .queryBrandRetailerConnection({ brandId: rBrandId, retailerId: rCompanyId })
      .onSnapshot((snapshot) => {
        setStatus(snapshot.docs[0]?.data()?.status);
      });

    return () => {
      unsubscribe();
    };
  }, [rBrandId, rCompanyId]);

  return status;
};

const usePromotionsAnnouncementsData = (): { promotions: tPromotion[]; announcements: tAnnouncement[] } => {
  const rBrandId = useSelector((state) => state.BrandLandingReducer.brandData.id);
  const [promotions, setPromotions] = React.useState<tPromotion[]>([]);
  const [announcements, setAnnouncements] = React.useState<tAnnouncement[]>([]);

  React.useEffect(() => {
    const unsubscribe = queryBrandPromotions(rBrandId, {
      isPrivate: false,
      isDeactivated: false,
      endDate: firebase.firestore.Timestamp.now(),
    }).onSnapshot((snapshot) => {
      setPromotions(snapshot.docs.map((doc) => doc.data()));
    });

    return () => {
      unsubscribe();
    };
  }, [rBrandId]);

  React.useEffect(() => {
    const unsubscribe = queryBrandAnnouncements(rBrandId, { endDate: firebase.firestore.Timestamp.now() }).onSnapshot(
      (snapshot) => {
        setAnnouncements(snapshot.docs.map((doc) => doc.data()));
      },
    );

    return () => {
      unsubscribe();
    };
  }, [rBrandId]);

  return { promotions, announcements };
};

type tButtonText = Record<tUserConnection["status"] | "Not Connected", string>;
const kButtonText: tButtonText = {
  RequestToRetailer: "Invite Pending",
  RequestToBrand: "Request Pending",
  Invited: "Invited",
  Pending: "Pending",
  Request: "Pending",
  Approved: "Connected",
  Declined: "Declined",
  Cancelled: "Cancelled",
  Deactivated: "Deactivated",
  "Not Connected": "Connect to Shop",
} as const;

const searchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
// const baseClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
// const searchClient: SearchClient = {
//   ...baseClient,
//   async search<TObject>(
//     requests: readonly MultipleQueriesQuery[],
//   ): Promise<Readonly<MultipleQueriesResponse<TObject>>> {
//     console.log("Algolia Search Requests:", JSON.stringify(requests, null, 2));
//     const response = await baseClient.search<TObject>(requests);
//     console.log("Algolia Search Response:", JSON.stringify(response, null, 2));
//     return response;
//   },
// };

const getProductQuantityAddedToCart = (
  cart: tDetailedCart[] | undefined,
  brandId: string,
  productId: string,
): number => {
  if (!cart) return 0;
  const brandCart = cart.find((_cart) => _cart.brandUid === brandId);
  if (!brandCart) return 0;
  const products = brandCart.products.filter((_product) => _product.productId === productId);
  if (products.length === 0) return 0;
  return products.reduce((acc, product) => acc + product.quantity, 0);
};

const PressPublication: React.FC<{ data: tBrandPressPublication; isLast: boolean }> = ({ data, isLast }) => {
  const logoPressUrl = dPressLogosUrls[data];

  if (!logoPressUrl) {
    return null;
  }

  return (
    <SLImage
      source={{ uri: logoPressUrl }}
      webStyle={isLast ? webStyles.logoPressLast : webStyles.logoPress}
      mobileStyle={isLast ? styles.logoPressLast : styles.logoPress}
    />
  );
};

const ProductCard: React.FC<{ hit: tAlgoliaProduct; brand: string }> = ({ hit: myHit, brand: myBrand }) => {
  const {
    objectID,
    name,
    brandUid,
    maxMsrpPrice,
    maxWhosalePrice,
    minMsrpPrice,
    minWhosalePrice,
    imageUrls,
    caseMinimum,
    sku,
    hasStock,
    hasVariants,
    hasTester,
  } = myHit;
  const rCart = useSelector(({ UserReducer }) => UserReducer.cart);
  const rDeviceSize = useSelector(({ NavigationReducer }) => NavigationReducer.deviceSize);
  const kNav = useNavigation();
  const dispatch = useAppDispatch();
  const addVariantProductModal = useModal<tAddProductModal, void>(AddProductModal);
  const rQuantityAdded = React.useMemo(
    () => getProductQuantityAddedToCart(rCart, brandUid, objectID),
    [brandUid, objectID, rCart],
  );
  const [isAddingCase, setAddingCase] = React.useState(false);
  const [isAddingTester, setAddingTester] = React.useState(false);

  const navigateToProduct = () => {
    kNav.navigate(kRoute.P101, { brand: myBrand, productId: objectID });
  };

  const handleOnPressAddCase = async () => {
    if (hasVariants) {
      addVariantProductModal.openAsync({ action: "add", productUid: objectID });
    } else {
      if (sku) {
        try {
          setAddingCase(true);
          await api.post({ endpoint: `/cart/${brandUid}/${objectID}/${sku}/${caseMinimum}/add`, body: undefined });
          dispatch({ type: "RIBBON", message: "Case added" });
        } catch (error) {
          dispatch({
            type: "RIBBON",
            message: new Error(
              isApiError(error) && error.response
                ? error.response?.data.message
                : "An error occurred please try again.",
            ),
          });
        } finally {
          setAddingCase(false);
        }
      } else {
        navigateToProduct();
      }
    }
  };

  const handleOnPressAddTester = async () => {
    if (sku) {
      try {
        setAddingTester(true);
        await api.post({ endpoint: `/cart/${brandUid}/${objectID}/${sku}/0/add`, body: { addTester: true } });
        dispatch({ type: "RIBBON", message: "Tester added" });
      } catch (error) {
        dispatch({
          type: "RIBBON",
          message: new Error(
            isApiError(error) && error.response ? error.response?.data.message : "An error occurred please try again.",
          ),
        });
      } finally {
        setAddingTester(false);
      }
    } else {
      navigateToProduct();
    }
  };

  return (
    <View style={rDeviceSize === "lg" ? styles.P100_BrandCardContainer : styles.P100_BrandCardContainerMobile}>
      <TouchableOpacity onPress={navigateToProduct}>
        <Carousel imageUrls={imageUrls} />
        {!!rQuantityAdded && (
          <View style={styles.P100_QuantityAdded}>
            <Text style={styles.P100_QuantityAddedLabel}>{rQuantityAdded}</Text>
          </View>
        )}
      </TouchableOpacity>
      <View style={styles.flex1}>
        <PdText numberOfLines={2} style={styles.productName}>
          {name}
        </PdText>
        <View style={[styles.flexRow, styles.alignCenter, styles.mb1]}>
          <Text style={fonts.H4_Selected_BrandDark}>{`${
            hasVariants ? formatMinMaxMoney(minWhosalePrice, maxWhosalePrice) : formatMoney(minWhosalePrice)
          } WSP`}</Text>
          <Text style={fonts.H4_BrandDark}>{`  |  ${
            hasVariants ? formatMinMaxMoney(minMsrpPrice, maxMsrpPrice) : formatMoney(minMsrpPrice)
          } MSRP`}</Text>
        </View>
        <Text style={[styles.P100_BrandCardPriceText, styles.mb1]}>{`Case of ${caseMinimum}`}</Text>
        <View style={styles.P100_BrandCardShopButtons}>
          {hasStock ? (
            <PdButton variant="link" isLoading={isAddingCase} onPress={handleOnPressAddCase}>
              <PdButtonLabel>{hasVariants ? "CHOOSE OPTIONS" : "ADD A CASE"}</PdButtonLabel>
            </PdButton>
          ) : (
            <View style={[styles.P100_BrandCardShopButton, styles.buttonDisabled]}>
              <Text style={fonts.SecondaryCTA_BrandDark}>OUT OF STOCK</Text>
            </View>
          )}
          {hasTester && !hasVariants && (
            <PdButton
              variant="link"
              contentContainerStyle={styles.borderBottomGray}
              isLoading={isAddingTester}
              onPress={handleOnPressAddTester}
              leftIcon={<PdAdd style={[styles.textGray, styles.widthXs]} />}
            >
              <PdButtonLabel style={styles.textGray}>TESTER</PdButtonLabel>
            </PdButton>
          )}
        </View>
      </View>
    </View>
  );
};

const SearchResults: React.FC<{ brand: string }> = ({ brand }) => {
  const { hits } = useHits<tAlgoliaProduct>();
  const numColumns = useGridLayout();
  const rows = chunk(hits, numColumns);

  return (
    <>
      {rows.map((row, rowIndex) => (
        <View key={rowIndex} style={styles.productRow}>
          {row.map((hit) => (
            <ProductCard key={hit.objectID} hit={hit} brand={brand} />
          ))}
          {row.length < numColumns &&
            times(numColumns - row.length).map((_, index) => <View key={index} style={styles.ghostProductCard} />)}
        </View>
      ))}
    </>
  );
};

const AnnouncementCard: React.FC<{ announcement: tAnnouncement; isLast: boolean }> = ({ announcement, isLast }) => {
  const rIsLargeScreen = useIsLargeScreenSelector();

  return (
    <PdProfileCard style={[styles.announcement, isLast ? [] : styles.mb3]}>
      <PdStack gap={6} layout={[1, 3]} direction={rIsLargeScreen ? "horizontal" : "vertical"} style={styles.flex1}>
        <PdStack gap={6} direction="horizontal" style={styles.alignCenter}>
          <View style={styles.marketingIconContainer}>
            <PdAnnounce style={styles.marketingIcon} color={kColors.white} />
          </View>
          <PdText variant="body-lg" style={styles.announcementTitle}>
            {announcement.title}
          </PdText>
        </PdStack>
        <PdStack
          gap={6}
          layout={[6, 1]}
          style={rIsLargeScreen ? styles.announcementDetailsContainer : styles.announcementDetailsContainerMobile}
          direction={rIsLargeScreen ? "horizontal" : "vertical"}
        >
          <PdText variant="body-lg" style={styles.marketingDescription}>
            {announcement.description}
          </PdText>
          <View style={styles.mrAuto}>
            <PdText style={styles.marketingTitle}>Ends:</PdText>
            <PdText style={styles.marketingDescription}>{`${moment(announcement.endDate.seconds * 1000 ?? 0).format(
              "MMMM Do, YYYY",
            )}`}</PdText>
          </View>
        </PdStack>
      </PdStack>
    </PdProfileCard>
  );
};

const PromotionCard: React.FC<{ promotion: tPromotion; isLast: boolean }> = ({ promotion, isLast }) => {
  const dispatch = useAppDispatch();
  const rIsLargeScreen = useIsLargeScreenSelector();
  const rCart = useSelector(({ BrandLandingReducer, UserReducer }) =>
    UserReducer.cart?.find((aCart) => aCart.brandUid === BrandLandingReducer.brandData.id),
  );

  const discountLabel = promotion.discountFixed
    ? `$${promotion.discountFixed}`
    : promotion.discountPercent
    ? formatPercentage(promotion.discountPercent)
    : "-";

  const promotionPress = () => {
    if (rCart) {
      try {
        dispatch(addPromoCode(rCart?.cartId, promotion.code));
      } catch (error) {
        if (isApiError(error)) {
          const message = error.response?.data?.message ?? "An error occurred please try again.";
          dispatch({ type: "RIBBON", message: new Error(message) });
        }
      }
    } else {
      dispatch({
        type: "RIBBON",
        message: new Error("Please add products to your cart before applying a promotion"),
      });
    }
  };

  return (
    <TouchableOpacity style={isLast ? [] : styles.mb3} onPress={promotionPress}>
      <PdProfileCard style={styles.promotionCard}>
        <PdStack gap={6} layout={[1, 1]} direction={rIsLargeScreen ? "horizontal" : "vertical"} style={styles.flex1}>
          <PdStack gap={6} direction="horizontal" style={styles.alignCenter}>
            <View style={styles.marketingIconContainer}>
              <PdGiftBox style={styles.marketingIcon} color={kColors.white} />
            </View>
            <PdText variant="body-lg" style={styles.promotionTitle}>{`Spend ${formatCurrency(
              promotion.enableAt,
            )} and receive ${discountLabel} off your order!`}</PdText>
          </PdStack>
          <PdStack
            gap={6}
            layout={[1, 1, 1, 1]}
            style={styles.marketingDetailsContainer}
            direction={rIsLargeScreen ? "horizontal" : "vertical"}
          >
            <View>
              <PdText style={styles.marketingTitle}>Code:</PdText>
              <PdText style={styles.marketingDescription}>{promotion.code || "-"}</PdText>
            </View>
            <View>
              <PdText style={styles.marketingTitle}>Ends:</PdText>
              <PdText style={styles.marketingDescription}>{`${moment(promotion.endDate ?? 0).format(
                "MMMM Do, YYYY",
              )}`}</PdText>
            </View>
            <View>
              <PdText style={styles.marketingTitle}>Discount:</PdText>
              <PdText style={styles.marketingDescription}>{discountLabel}</PdText>
            </View>
            <View>
              <PdText style={styles.marketingTitle}>Minimum Order:</PdText>
              <PdText style={styles.marketingDescription}>{formatCurrency(promotion.enableAt)}</PdText>
            </View>
          </PdStack>
        </PdStack>
      </PdProfileCard>
    </TouchableOpacity>
  );
};

const HiddenRefinementList: React.FC<{ attribute: string; defaultRefinement: string }> = ({
  attribute,
  defaultRefinement,
}) => {
  const { refine } = useRefinementList({ attribute });

  React.useEffect(() => {
    refine(defaultRefinement);
  }, [defaultRefinement, refine]);

  return null;
};

type tpBrandLanding = (props: { route: RouteProp<tRouteParams, typeof kRoute.P100> }) => React.ReactElement;
const P100_BrandLanding: tpBrandLanding = ({ route }) => {
  const myBrand = route.params?.brand;
  const { promotions, announcements } = usePromotionsAnnouncementsData();
  useFetchBrandLanding(myBrand);

  const dispatch = useAppDispatch();
  const kNav = useNavigation();
  const rIsLargeScreen = useIsLargeScreenSelector();
  const rCompanyId = useSelector(({ CompanyReducer }) => (CompanyReducer.type === "brand" ? CompanyReducer.id : ""));
  const rCompanyName = useSelector(({ CompanyReducer }) =>
    CompanyReducer.type === "brand" ? CompanyReducer.name : "",
  );
  const rCurrentUserType = useSelector(({ CompanyReducer }) => CompanyReducer?.type);
  const rBrandName = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.name);
  const rBrandTermsConditionURL = useSelector(
    ({ BrandLandingReducer }) => BrandLandingReducer.brandData.termsAndConditions.url,
  );
  const rBrandId = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.id);
  const rBrandEthos = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.brandEthos);
  const rIsExclusive = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.isExclusive);
  const rBrandLinks = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.links);
  const rBrandWebsite = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.website);
  const rBrandCity = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.addresses[0].city);
  const rBrandOpeningMin = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.openingMin);
  const rBrandReorderMin = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.reorderMin);
  const rBrandStory = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.brandStory);
  const rBrandState = useSelector(
    ({ BrandLandingReducer }) => BrandLandingReducer.brandData.addresses[0].state_or_province,
  );
  const rBrandCountry = useSelector(
    ({ BrandLandingReducer }) => BrandLandingReducer.brandData.addresses[0].country_code,
  );
  const rBrandCarouselImages = useSelector(
    ({ BrandLandingReducer }) => BrandLandingReducer.brandData.images.heroes.map((hero) => hero.downloadURL) || [],
  );
  const rBrandImageLogoURL = useSelector(
    ({ BrandLandingReducer }) => BrandLandingReducer.brandData.images.logoDownloadURL,
  );
  const rBrandVideoUrl = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.videoUrl);
  const rBrandAssetsFolderUrl = useSelector(({ BrandLandingReducer }) => BrandLandingReducer.brandData.assetsFolderUrl);
  const rBrandPressPublications = useSelector(
    ({ BrandLandingReducer }) => BrandLandingReducer.brandData.pressPublications || [],
  );
  const rBrandConnectionStatus = useConnectionStatusSelector();
  const rLoading = useSelector((state) => state.LoadingReducer.companyFetching);
  const rMessageThreadId = useSelector(({ MessageThreadsReducer }) => {
    return MessageThreadsReducer.rThreads.find((myThread: tThread) => {
      const kPatternMatches = myThread.recipient.match(/\/(\w|\d|-)+/);
      if (kPatternMatches === null) throw new Error("An error occurred while attempting to parse the recipient's id.");
      return rBrandId === kPatternMatches[0].replace("/", "");
    })?.id;
  });
  const toast = useToast();
  const connectionRequestModal = useConnectionRequestModal();
  const brandReadMoreModal = useBrandReadMoreModal();
  const scrollViewRef = React.useRef<ScrollView>(null);

  const publicPromotions = React.useMemo(
    () => promotions.filter((promotion) => promotion.discountFixed || promotion.discountPercent),
    [promotions],
  );

  const handleOpenTermsAndConditions = () => {
    openUrlInNewTab(getSafeUrl(rBrandTermsConditionURL));
  };

  const handleOpenWebsite = () => {
    openUrlInNewTab(getSafeUrl(rBrandWebsite));
  };

  const handleOpenSocialMedia = (socialMediaLink?: string) => {
    openUrlInNewTab(getSafeUrl(socialMediaLink));
  };

  const _handleConnectWithBrand = async () => {
    const response = await connectionRequestModal.openAsync({ brandId: rBrandId, brandName: rBrandName });
    if (response.status === "closed") return;
    toast({ description: "Your connection request was sent." });
  };

  const handleMessageBrand = () => {
    kNav.navigate(kRoute.P04);
    if (rMessageThreadId) dispatch(setCurrentThread(rMessageThreadId));
    else throw new Error("Could not extract message thread id");
  };

  const handleOnPressReadMore = () => {
    brandReadMoreModal.openAsync();
  };

  const scrollToTop = () => {
    scrollViewRef.current?.scrollTo({ x: 0, y: rIsLargeScreen ? 700 : 0 });
  };

  // ensures a brand doesn't see other brand's page
  React.useEffect(() => {
    if (
      rCurrentUserType === "brand" &&
      !!rCompanyId &&
      !!rBrandId &&
      rBrandId !== kNilUuid &&
      rCompanyId !== rBrandId
    ) {
      kNav.navigate(kRoute.P100, { brand: rCompanyName });
    }
  }, [kNav, rBrandId, rCompanyId, rCompanyName, rCurrentUserType]);

  React.useEffect(() => {
    searchClient.clearCache();
  }, []);

  // const logClient = algoliasearch(ALGOLIA_APP_ID, "f7efaf87311845d49fa756ce6660af52");
  // logClient
  //   .getLogs({ offset: 0, length: 10, queryParameters: { filters: `brandUid:${rBrandId}` }, type: "query" })
  //   .then((logs) => {
  //     console.log("Algolia Logs", logs);
  //   })
  //   .catch((error) => {
  //     console.error("Algolia Logs Error", error);
  //   });
  return (
    <InstantSearch searchClient={searchClient} indexName={ALGOLIA_PRODUCTS.LAST_MODIFIED}>
      <ShopSubHeader />
      {rLoading || myBrand !== rBrandName ? (
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
        </View>
      ) : (
        <ScrollView ref={scrollViewRef}>
          <View style={styles.P100_HeaderBanner}>
            <ScrollView horizontal contentContainerStyle={styles.banner}>
              {rBrandCarouselImages.map((uri, index) => (
                <SLImage
                  key={index}
                  source={{ uri }}
                  mobileStyle={styles.carouselImage}
                  webStyle={webStyles.carouselImage}
                />
              ))}
            </ScrollView>
          </View>

          <View style={rIsLargeScreen ? [styles.flexRow, styles.mx20, styles.mb6] : [styles.mx5, styles.mb3]}>
            <SLImage
              mobileStyle={[rIsLargeScreen ? [] : styles.alignMeCenter, styles.P100_Icon]}
              webStyle={{ ...(!rIsLargeScreen && { ...styles.alignMeCenter }), ...styles.P100_Icon }}
              source={{ uri: rBrandImageLogoURL }}
              showFallbackLabel
            />
            <View style={rIsLargeScreen ? [styles.mt6, styles.ml8, styles.flex1] : [styles.mt3, styles.mb3]}>
              <View style={[styles.flexRow, styles.alignCenter]}>
                <Text style={styles.P100_BrandName}>{rBrandName}</Text>
                {rIsExclusive && (
                  <PdTag style={styles.ml4}>
                    <PdTagLabel>EXCLUSIVE</PdTagLabel>
                  </PdTag>
                )}
              </View>
              <Text style={styles.P100_BrandAddress}>{`${rBrandCity}, ${
                rBrandState !== "" || undefined ? rBrandState : rBrandCountry
              }`}</Text>
            </View>
            <PdStack
              direction={rIsLargeScreen ? "horizontal" : "vertical"}
              style={rIsLargeScreen ? [styles.mt6, styles.ml8, styles.flex1] : [styles.mt3, styles.flex1]}
            >
              {rBrandConnectionStatus === "Approved" && (
                <View style={styles.connectedLabelContainer}>
                  <PdText style={styles.connectedLabel}>Connected</PdText>
                  <View style={styles.connectedLabelIconContainer}>
                    <PdCheck color={kColors.white} style={styles.checkIcon} />
                  </View>
                </View>
              )}
              <View style={styles.flex1}>
                {rCurrentUserType !== "brand" && rBrandConnectionStatus === "Approved" && (
                  <PdButton onPress={handleMessageBrand} size="lg" variant="outline" style={styles.mb5}>
                    <PdButtonLabel>Message the Brand</PdButtonLabel>
                  </PdButton>
                )}
                {rCurrentUserType === "retailer" && rBrandConnectionStatus !== "Approved" && (
                  <PdButton onPress={_handleConnectWithBrand} size="lg">
                    <PdButtonLabel>{kButtonText[rBrandConnectionStatus || "Not Connected"]}</PdButtonLabel>
                  </PdButton>
                )}
              </View>
            </PdStack>
          </View>

          {announcements.length > 0 && (
            <View style={rIsLargeScreen ? [styles.mx20, styles.mb3] : [styles.mx5, styles.mb3]}>
              {announcements.map((announcement, index) => (
                <AnnouncementCard
                  key={announcement.id}
                  announcement={announcement}
                  isLast={index === announcements.length - 1}
                />
              ))}
            </View>
          )}

          {publicPromotions.length > 0 && (
            <View style={rIsLargeScreen ? [styles.mx20, styles.mb3] : [styles.mx5, styles.mb3]}>
              {publicPromotions.map((promotion, index) => (
                <PromotionCard
                  key={promotion.id}
                  promotion={promotion}
                  isLast={index === publicPromotions.length - 1}
                />
              ))}
            </View>
          )}

          <PdStack
            gap={3}
            layout={[1, 1]}
            direction={rIsLargeScreen ? "horizontal" : "vertical"}
            style={rIsLargeScreen ? [styles.mx20] : [styles.mx5]}
          >
            <PdProfileCard>
              <PdStack gap={3} layout={[1, 1]} direction={rIsLargeScreen ? "horizontal" : "vertical"}>
                <PdStack gap={3} direction="vertical">
                  <View>
                    <PdText style={styles.cardTitle}>Opening Minimum</PdText>
                    <PdText>{formatCurrency(rBrandOpeningMin)}</PdText>
                  </View>
                  <View>
                    <PdText style={styles.cardTitle}>Brand Video</PdText>
                    {rBrandVideoUrl ? (
                      <PdProfileSocialIconButton
                        style={styles.mrAuto}
                        onPress={() => handleOpenSocialMedia(rBrandVideoUrl)}
                        icon={<PdPlayFill style={styles.brandVideoIcon} />}
                      />
                    ) : (
                      <PdText>-</PdText>
                    )}
                  </View>
                  <View>
                    <PdText style={styles.cardTitle}>Brand Assets</PdText>
                    {rBrandAssetsFolderUrl ? (
                      <PdProfileSocialIconButton
                        variant="outline"
                        style={styles.mrAuto}
                        onPress={() => handleOpenSocialMedia(rBrandAssetsFolderUrl)}
                        icon={<PdArrowUpRight style={styles.brandAssetsIcon} />}
                      />
                    ) : (
                      <PdText>-</PdText>
                    )}
                  </View>
                  <View>
                    <PdText style={styles.cardTitle}>Ethos</PdText>
                    {rBrandEthos.length > 0 ? (
                      <View style={styles.ethosContainer}>
                        {rBrandEthos.map((ethos) => {
                          const _ethos = kBrandEthosConversion[ethos];
                          if (!_ethos) return null;
                          return (
                            <PdProfileTag key={_ethos}>
                              <PdProfileTagLabel>{_ethos}</PdProfileTagLabel>
                            </PdProfileTag>
                          );
                        })}
                      </View>
                    ) : (
                      <PdText>-</PdText>
                    )}
                  </View>
                </PdStack>
                <PdStack gap={3} direction="vertical">
                  <View>
                    <PdText style={styles.cardTitle}>Reorder Minimum</PdText>
                    <PdText>{formatCurrency(rBrandReorderMin)}</PdText>
                  </View>
                  <View>
                    <PdText style={styles.cardTitle}>Website</PdText>
                    <TouchableOpacity onPress={handleOpenWebsite} style={styles.profileButton}>
                      <PdText style={styles.cardLabel}>
                        {rBrandWebsite.replace("https://", "").replace("http://", "")?.toLocaleLowerCase()}
                      </PdText>
                      <PdArrowUpRight style={styles.arrowUpIcon} color={kColors.brandDark} />
                    </TouchableOpacity>
                  </View>
                  <View>
                    <PdText style={styles.cardTitle}>Socials</PdText>
                    {rBrandLinks.facebook || rBrandLinks.instagram ? (
                      <View style={styles.rowContainer}>
                        {rBrandLinks.facebook && (
                          <PdProfileSocialIconButton
                            style={styles.mr3}
                            onPress={() => handleOpenSocialMedia(rBrandLinks.facebook)}
                            icon={<PdFacebookAlt style={styles.sizeXs} />}
                          />
                        )}
                        {rBrandLinks.instagram && (
                          <PdProfileSocialIconButton
                            onPress={() => handleOpenSocialMedia(rBrandLinks.instagram)}
                            icon={<PdInstagram style={styles.sizeXs} />}
                          />
                        )}
                      </View>
                    ) : (
                      <PdText style={styles.mb3}>-</PdText>
                    )}
                  </View>
                  <TouchableOpacity style={[styles.flexRow, styles.alignCenter]} onPress={handleOpenTermsAndConditions}>
                    <View style={[styles.P100_AttachmentLinkIcon, styles.mr2]}>
                      <AttachmentIcon />
                    </View>
                    <PdText style={styles.cardLabel}>Terms & Conditions</PdText>
                  </TouchableOpacity>
                </PdStack>
              </PdStack>
            </PdProfileCard>
            <View>
              {rBrandPressPublications.length > 0 && (
                <PdProfileCard style={[styles.flexRow, styles.mb3]}>
                  <View style={styles.cardTitleInlineContainer}>
                    <PdText style={styles.cardTitleInline}>As Seen On</PdText>
                  </View>
                  <View style={styles.rowContainer}>
                    {rBrandPressPublications.map((pressPublication, pressPublicationIndex) => (
                      <PressPublication
                        key={pressPublication}
                        data={pressPublication}
                        isLast={pressPublicationIndex === rBrandPressPublications.length - 1}
                      />
                    ))}
                  </View>
                </PdProfileCard>
              )}
              <PdProfileCard style={styles.flex1}>
                <PdText style={styles.cardTitle}>{`About ${rBrandName}`}</PdText>
                <PdText numberOfLines={10}>{rBrandStory || "-"}</PdText>
                <TouchableOpacity style={styles.readMoreButton} onPress={handleOnPressReadMore}>
                  <PdText style={styles.cardTitleInline}>Read More</PdText>
                </TouchableOpacity>
              </PdProfileCard>
            </View>
          </PdStack>

          <View
            style={
              rIsLargeScreen
                ? [styles.mx20, styles.mt6, styles.mb6, styles.flex1]
                : [styles.mx5, styles.mt3, styles.mb3, styles.flex1]
            }
          >
            <View style={rIsLargeScreen ? styles.flexRow : {}}>
              <View style={rIsLargeScreen ? [styles.P100_LeftSideMenu, styles.mr8, styles.mt15] : {}}>
                <View style={[styles.mb6, styles.zIndexDocked]}>
                  <SortBy
                    items={[
                      { value: ALGOLIA_PRODUCTS.LAST_MODIFIED, label: "Recently Added" },
                      { value: ALGOLIA_PRODUCTS.PRICE_ASC, label: "Price Low to High" },
                      { value: ALGOLIA_PRODUCTS.PRICE_DESC, label: "Price High to Low" },
                    ]}
                    style={styles.mb6}
                  />
                </View>
                <CategoryRefinement searchKey="products" />
                <HiddenRefinementList attribute="brandUid" defaultRefinement={rBrandId} />
                <HiddenRefinementList attribute="isPublished" defaultRefinement="true" />
              </View>
              <View style={styles.flex1}>
                <SearchBox searchKey="products" style={[styles.mb10, rIsLargeScreen ? styles.ml5 : styles.mt5]} />
                <SearchResults brand={myBrand} />
              </View>
            </View>
            <SearchPagination onPress={scrollToTop} />
          </View>
        </ScrollView>
      )}
    </InstantSearch>
  );
};

export const P100 = React.memo(P100_BrandLanding);

const styles = StyleSheet.create({
  P100_AttachmentLinkIcon: {
    height: 14,
    width: 13,
  },
  P100_BrandAddress: {
    ...kFonts.poppinsRegularLight,
    color: kColors.brandDark,
    letterSpacing: 1.1,
    lineHeight: 30,
  },
  P100_BrandCardContainer: {
    alignItems: "stretch",
    flex: 1,
    flexDirection: "column",
    height: 550,
    justifyContent: "flex-start",
    marginBottom: 14 * kStyleConst.kSpacing,
    marginLeft: 5 * kStyleConst.kSpacing,
  },
  P100_BrandCardContainerMobile: {
    alignItems: "stretch",
    flexBasis: 320,
    flexDirection: "column",
    flexShrink: 1,
    flex: 1,
    height: 550,
    justifyContent: "flex-start",
    marginBottom: 14 * kStyleConst.kSpacing,
  },
  P100_BrandCardPriceText: {
    ...kFonts.poppinsLight,
    color: kColors.black,
  },
  P100_BrandCardShopButton: {
    borderBottomColor: kColors.black,
    borderBottomWidth: 1.75,
    width: 115,
  },
  P100_BrandCardShopButtons: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 3.5 * kStyleConst.kSpacing,
  },
  P100_BrandName: {
    ...kFonts.poppinsExtraLargeMedium,
    color: kColors.brandDark,
    letterSpacing: 1.4,
    lineHeight: 40,
  },
  P100_HeaderBanner: {
    height: kStyleConst.kBrandBannerHeight,
    overflow: "hidden",
    width: "100%",
  },
  P100_Icon: {
    backgroundColor: kColors.gray400,
    borderRadius: 999,
    height: kStyleConst.kBrandIconDiameter,
    marginTop: -12 * kStyleConst.kSpacing,
    width: kStyleConst.kBrandIconDiameter,
  },
  P100_LeftSideMenu: {
    width: 200,
  },
  P100_QuantityAdded: {
    backgroundColor: kColors.lightBrand,
    borderRadius: 4,
    left: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    position: "absolute",
    top: 10,
  },
  P100_QuantityAddedLabel: {
    color: kColors.brandDarkRed,
    fontFamily: "Poppins",
    fontSize: 12,
  },
  alignCenter: {
    alignItems: "center",
  },
  alignMeCenter: {
    alignSelf: "center",
  },
  announcement: {
    alignItems: "center",
    backgroundColor: kColors.green500,
    flexDirection: "row",
    paddingVertical: 4 * kStyleConst.kSpacing,
  },
  announcementDetailsContainer: {
    alignItems: "center",
    flex: 1,
  },
  announcementDetailsContainerMobile: {
    alignItems: "flex-start",
    flex: 1,
  },
  announcementTitle: {
    color: kColors.white,
    flex: 1,
    flexWrap: "nowrap",
    marginRight: 8 * kStyleConst.kSpacing,
  },
  arrowUpIcon: {
    height: 12,
    marginLeft: kStyleConst.kSpacing,
    width: 12,
  },
  banner: {
    alignItems: "center",
    flexGrow: 1,
    justifyContent: "center",
    margin: kStyleConst.kSpacing,
  },
  borderBottomGray: {
    borderColor: kColors.gray500,
  },
  brandAssetsIcon: {
    height: 10,
    width: 10,
  },
  brandVideoIcon: {
    height: 12,
    marginLeft: 2,
    width: 12,
  },
  buttonDisabled: {
    opacity: 0.4,
  },
  cardLabel: {
    fontSize: 12,
    fontWeight: "400",
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "400",
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  cardTitleInline: {
    fontSize: 12,
    fontWeight: "400",
  },
  cardTitleInlineContainer: {
    width: 120,
  },
  carouselImage: {
    backgroundColor: kColors.backgroundGrey,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 0,
    height: 280,
    margin: kStyleConst.kSpacing,
    width: 280,
  },
  checkIcon: {
    height: 10,
    width: 10,
  },
  connectedLabel: {
    color: kColors.green,
    marginRight: 3 * kStyleConst.kSpacing,
  },
  connectedLabelContainer: {
    alignItems: "center",
    flexDirection: "row",
    height: 56,
    justifyContent: "center",
    paddingHorizontal: kStyleConst.kSpacing * 8,
  },
  connectedLabelIconContainer: {
    alignItems: "center",
    backgroundColor: kColors.green,
    borderRadius: 999,
    height: 20,
    justifyContent: "center",
    width: 20,
  },
  ethosContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 2 * kStyleConst.kSpacing,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  ghostProductCard: {
    flex: 1,
  },
  loadingView: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  logoPress: {
    height: 40,
    marginRight: 3 * kStyleConst.kSpacing,
    width: 80,
  },
  logoPressLast: {
    height: 40,
    width: 80,
  },
  marketingDescription: {
    color: kColors.white,
  },
  marketingDetailsContainer: {
    alignItems: "flex-start",
    flex: 1,
  },
  marketingIcon: {
    height: 35,
    width: 35,
  },
  marketingIconContainer: {
    color: kColors.white,
    marginRight: 8 * kStyleConst.kSpacing,
  },
  marketingTitle: {
    color: kColors.white,
    fontSize: 12,
    fontWeight: "400",
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb1: {
    marginBottom: kStyleConst.kSpacing,
  },
  mb10: {
    marginBottom: 10 * kStyleConst.kSpacing,
  },
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb5: {
    marginBottom: 5 * kStyleConst.kSpacing,
  },
  mb6: {
    marginBottom: 6 * kStyleConst.kSpacing,
  },
  ml4: {
    marginLeft: 4 * kStyleConst.kSpacing,
  },
  ml5: {
    marginLeft: 5 * kStyleConst.kSpacing,
  },
  ml8: {
    marginLeft: 8 * kStyleConst.kSpacing,
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  mr3: {
    marginRight: 3 * kStyleConst.kSpacing,
  },
  mr8: {
    marginRight: 8 * kStyleConst.kSpacing,
  },
  mrAuto: {
    marginRight: "auto",
  },
  mt15: {
    marginTop: 15 * kStyleConst.kSpacing,
  },
  mt3: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  mt5: {
    marginTop: 5 * kStyleConst.kSpacing,
  },
  mt6: {
    marginTop: 6 * kStyleConst.kSpacing,
  },
  mx20: {
    marginHorizontal: 20 * kStyleConst.kSpacing,
  },
  mx5: {
    marginHorizontal: 5 * kStyleConst.kSpacing,
  },
  productName: {
    color: kColors.black,
    fontFamily: "Poppins",
    fontSize: 16,
    fontWeight: "500",
    marginBottom: "auto",
    marginVertical: 3 * kStyleConst.kSpacing,
  },
  productRow: {
    flex: 1,
    flexDirection: "row",
  },
  profileButton: {
    alignItems: "center",
    flexDirection: "row",
  },
  promotionCard: {
    alignItems: "center",
    backgroundColor: kColors.brandDarkRed,
    flexDirection: "row",
    paddingVertical: 4 * kStyleConst.kSpacing,
  },
  promotionTitle: {
    color: kColors.white,
  },
  readMoreButton: {
    marginRight: "auto",
    marginTop: "auto",
    paddingTop: 4 * kStyleConst.kSpacing,
  },
  rowContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    flex: 1,
  },
  sizeXs: {
    height: 12,
    width: 12,
  },
  textGray: {
    color: kColors.gray500,
  },
  widthXs: {
    width: 10,
  },
  zIndexDocked: {
    zIndex: 10,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  carouselImage: {
    backgroundColor: kColors.backgroundGrey,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 0,
    height: 280,
    margin: kStyleConst.kSpacing,
    width: 280,
  },
  logoPress: {
    height: 40,
    marginRight: 3 * kStyleConst.kSpacing,
    width: 80,
  },
  logoPressLast: {
    height: 40,
    width: 80,
  },
  P100_BrandCardImage: {
    alignSelf: "stretch",
    height: 380,
  },
  P100_BrandCardImagePlaceholder: {
    alignSelf: "stretch",
    backgroundColor: kColors.gray,
    height: 380,
  },
  P100_Icon: {
    backgroundColor: kColors.gray400,
    borderRadius: 999,
    height: kStyleConst.kBrandIconDiameter,
    marginTop: -12 * kStyleConst.kSpacing,
    width: kStyleConst.kBrandIconDiameter,
  },
  P100_IconMobile: {
    borderRadius: kStyleConst.kBrandIconMobileDiameter / 2,
    height: kStyleConst.kBrandIconMobileDiameter,
    width: kStyleConst.kBrandIconMobileDiameter,
  },
} as const;
